import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const slides = [
  {
    url: '/assets/images/hero-slide-1.png',
  },
  {
    url: '/assets/images/hero-slide-2.png',
  },
  {
    url: '/assets/images/hero-slide-3.png',
  },
];

export default function Hero() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const els = [titleRef.current, subRef.current, btnRef.current];
    els.forEach((el, i) => {
      if (el) {
        el.style.opacity = '0';
        el.style.transform = 'translateY(40px)';
        setTimeout(() => {
          if (el) {
            el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
          }
        }, 200 + i * 200);
      }
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
        setFading(false);
      }, 600);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  const goTo = (idx: number) => {
    if (idx === current) return;
    setFading(true);
    setTimeout(() => {
      setCurrent(idx);
      setFading(false);
    }, 600);
  };

  const handleScroll = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleContactClick = () => {
    navigate('/iletisim');
  };

  return (
    <section
      id="ana-sayfa"
      className="relative w-full h-screen min-h-[600px] flex items-center justify-center overflow-hidden"
    >
      {/* Slider Background */}
      {slides.map((slide, idx) => (
        <img
          key={idx}
          src={slide.url}
          alt=""
          loading="eager"
          fetchPriority={idx === 0 ? 'high' : 'low'}
          className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700"
          style={{
            opacity: idx === current ? (fading ? 0 : 1) : 0,
          }}
        />
      ))}

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/70" />

      {/* Content */}
      <div className="relative z-10 w-full text-center px-4 md:px-8 flex flex-col items-center">
        <span className="inline-block mb-6 px-4 py-1.5 rounded-full border border-orange-400/60 text-orange-300 text-xs font-semibold tracking-widest uppercase">
          Yurtiçi &rsaquo; Yurtdışı &rsaquo; Uluslararası Lojistik
        </span>

        <h1
          ref={titleRef}
          className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight mb-4"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          SINIR TANIMAYAN<br />
          <span className="text-orange-400">LOJİSTİK</span>
        </h1>

        <p
          ref={subRef}
          className="text-xl md:text-2xl text-orange-200 font-semibold tracking-wide mb-4"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Güvenle Taşıyoruz
        </p>

        <p
          className="text-base md:text-lg text-gray-300 font-light max-w-2xl leading-relaxed mb-10"
        >
          Kilis merkezli Global79 Lojistik olarak Türkiye, Suriye, Azerbaycan, Irak,{' '}
          İran, Gürcistan, Ermenistan ve Rusya&apos;ya hızlı ve güvenilir lojistik çözümleri.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <button
            ref={btnRef}
            onClick={() => handleScroll('hizmetler')}
            className="group flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <span className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
              <i className="ri-truck-line text-white text-base" />
            </span>
            HİZMETLERİMİZİ KEŞFET
            <i className="ri-arrow-right-line text-white text-lg group-hover:translate-x-1 transition-transform" />
          </button>

          <a
            href="/iletisim"
            className="flex items-center gap-2 border-2 border-white/60 hover:border-white text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-white/10 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line" />
            Bize Ulaşın
          </a>
        </div>

        {/* Stats */}
        <div className="mt-14 flex flex-wrap justify-center gap-8 md:gap-16">
          {[
            { value: '8', label: 'Hizmet Ülkesi' },
            { value: '15+', label: 'Yıllık Tecrübe' },
            { value: '500+', label: 'Mutlu Müşteri' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl md:text-4xl font-extrabold text-orange-400">{stat.value}</div>
              <div className="text-sm text-gray-300 mt-1 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Slider Dots */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-3 z-20">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              idx === current
                ? 'w-8 h-2.5 bg-orange-400'
                : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/70'
            }`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <span className="text-white/40 text-xs tracking-widest uppercase">Keşfet</span>
        <i className="ri-arrow-down-line text-white/40 text-xl" />
      </div>
    </section>
  );
}
